import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from '../../../store/store';

/** The whole household, rather than one learner. */
export const ALL_LEARNERS = 'ALL';

export interface LearnerOption {
  studentId: string;
  name: string;
}

/**
 * PRT-14-FR-04 — which learner a family screen is showing.
 *
 * Starts from the learner the parent is acting as (the console's context
 * switcher), or the whole household in the parent's own view, and follows the
 * switcher when it changes. The filter lives in the screen's state, not the
 * URL, so no address carries a learner id (UC-P08 AC-25).
 */
export function useHouseholdFilter(): [string, (value: string) => void] {
  const activeLearner = useSelector((s: RootState) => s.context.activeContext?.studentId ?? null);
  const [filter, setFilter] = useState<string>(activeLearner ?? ALL_LEARNERS);
  useEffect(() => {
    setFilter(activeLearner ?? ALL_LEARNERS);
  }, [activeLearner]);
  return [filter, setFilter];
}

/** Learner names are text, not only a colour (UC-P08 §7, accessibility). */
const LearnerFilter: React.FC<{
  learners: LearnerOption[];
  value: string;
  onChange: (value: string) => void;
}> = ({ learners, value, onChange }) => {
  if (learners.length < 2 && value === ALL_LEARNERS) return null;
  const options = [{ studentId: ALL_LEARNERS, name: 'Toda la familia' }, ...learners];
  return (
    <div
      role="group"
      aria-label="Alumno"
      style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '12px 0' }}
    >
      {options.map((o) => (
        <button
          key={o.studentId}
          type="button"
          aria-pressed={o.studentId === value}
          className={`btn btn-sm ${o.studentId === value ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => onChange(o.studentId)}
        >
          {o.name}
        </button>
      ))}
    </div>
  );
};

export default LearnerFilter;
